<script setup>
import { computed } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, ArcElement } from 'chart.js';

const props = defineProps({
  collection: {
    type: Object,
    default: () => ({}),
  },
  chartOptions: {
    type: Object,
    default: () => ({}),
  },
});

// Draws each slice's raw value at its arc midpoint so the count is visible
// without hovering (tooltip alone wasn't enough). Skips slivers under 5%
// of the total -- text on a sliver that thin just overlaps illegibly.
const drawSegmentValues = {
  id: 'drawSegmentValues',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      const total = dataset.data.reduce((sum, value) => sum + value, 0);
      if (!total) return;

      meta.data.forEach((arc, index) => {
        const value = dataset.data[index];
        if (!value || value / total < 0.05) return;

        const { x, y, innerRadius, outerRadius, startAngle, endAngle } =
          arc.getProps(
            ['x', 'y', 'innerRadius', 'outerRadius', 'startAngle', 'endAngle'],
            true
          );
        const angle = (startAngle + endAngle) / 2;
        const radius = (innerRadius + outerRadius) / 2;
        const labelX = x + Math.cos(angle) * radius;
        const labelY = y + Math.sin(angle) * radius;

        ctx.save();
        ctx.font = 'bold 11px Inter, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
        ctx.strokeText(String(value), labelX, labelY);
        ctx.fillStyle = '#fff';
        ctx.fillText(String(value), labelX, labelY);
        ctx.restore();
      });
    });
  },
};

ChartJS.register(Title, Tooltip, ArcElement, drawSegmentValues);

const defaultChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '65%',
  animation: {
    duration: 0,
  },
  plugins: {
    legend: {
      // Callers render their own legend (label name + % + count), matching
      // the pattern already used by CsatRatingDistribution.vue.
      display: false,
    },
  },
};

const options = computed(() => {
  return { ...defaultChartOptions, ...props.chartOptions };
});
</script>

<template>
  <Doughnut :data="collection" :options="options" />
</template>
